import { SectionTitle } from "@/components/SectionHeader";
import { Eyebrow } from "@/components/Eyebrow";
import { SITE } from "@/lib/site";
import contactHero from "@/assets/contact-hero.webp?w=640;960;1280;1920;2560;3780&format=webp&as=meta";
import { responsiveImage } from "@/lib/responsiveImage";
import { CtaBanner } from "@/components/CtaBanner";
import { FormCard, FormRow, FormSection } from "@/components/FormCard";
import { CheckboxGroup, Field, Textarea } from "@/components/form/FormFields";
import { PageShell } from "@/components/PageShell";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/Button";
import { ContactValues, TOPICS } from "@/pages/contactForm";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

export function ContactPage() {
  return (
    <PageShell className="bg-secondary">
      <PageHero
        image={responsiveImage(contactHero)}
        imagePosition="70% center"
        imageAlt="Somo keypad on a fabric headboard above a bedside table"
        title="Contact Us"
        body="Have a question or want to connect? Tell us what’s on your mind and we’ll get you to the right person."
      />
      <ContactForm />
      <CtaBanner
        eyebrow="Interested in Somo for your property?"
        body="Tell us about your project and our team can help you find the right solution."
        cta={{ label: "Get A Quote", href: `${SITE}/get-a-quote` }}
      />
    </PageShell>
  );
}

function ContactForm() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful, isSubmitting },
  } = useForm<ContactValues>({
    resolver: zodResolver(ContactValues),
    defaultValues: { topics: [] },
  });

  if (isSubmitSuccessful) {
    return (
      <FormCard>
        <SubmittedMessage />
      </FormCard>
    );
  }

  return (
    <FormCard spaceBelow="loose">
      <form
        noValidate
        onSubmit={handleSubmit((values) => console.info("contact", values))}
        className="flex flex-col gap-12 md:gap-16"
      >
        <FormSection eyebrow="Contact Information">
          <FormRow>
            <Field
              label="First Name"
              autoComplete="given-name"
              error={errors.firstName?.message}
              {...register("firstName")}
            />
            <Field
              label="Last Name"
              autoComplete="family-name"
              error={errors.lastName?.message}
              {...register("lastName")}
            />
          </FormRow>
          <FormRow>
            <Field
              label="Email Address"
              type="email"
              autoComplete="email"
              error={errors.email?.message}
              {...register("email")}
            />
            <Field
              label="Phone Number"
              type="tel"
              autoComplete="tel"
              error={errors.phone?.message}
              {...register("phone")}
            />
          </FormRow>
        </FormSection>

        <div className="flex flex-col gap-10">
          <CheckboxGroup
            legend="What can we help you with?"
            options={TOPICS}
            registration={register("topics")}
            error={errors.topics?.message}
          />
          <Textarea
            label="Your Message"
            error={errors.message?.message}
            {...register("message")}
          />
        </div>

        <Button
          type="submit"
          size="lg"
          disabled={isSubmitting}
          className="self-start"
        >
          Send Request
        </Button>
      </form>
    </FormCard>
  );
}

export function SubmittedMessage() {
  return (
    <div className="flex flex-col gap-4 py-8 text-center">
      <Eyebrow>Thank you</Eyebrow>
      <SectionTitle>We’ve got your message</SectionTitle>
      <p className="text-base leading-normal tracking-body text-secondary">
        Someone from the Somo team will be in touch shortly.
      </p>
    </div>
  );
}
